import {naturalSortByKey, noop, objectFromEntries} from '@benchling/js-utils';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import AliasField from 'metadata/AliasField';
import {DraggableAliasField, DraggableAliasFieldProps} from 'metadata/DraggableAliasField';
import FieldActionButtons from 'metadata/FieldActionButtons';
import notifications from 'notifications';
import {
  ExistingSchemaFieldSelectorOption,
  SchemaFieldSelectorOptionForUpdate,
} from 'schemas/SchemaFieldSelectorConstants';
import ImportSchemaFieldSelectorOptionsModal from 'settings/registry/ImportSchemaFieldSelectorOptionsModal';
import FormInput from 'ui-library/bs/FormInput';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import AddIcon from 'ui-library/icons/AddIcon';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import runModal from 'ui-library/runModal';
import {InputChangeEvent} from 'util/Types';

export function isExistingSchemaFieldSelectorOption(
  option: SchemaFieldSelectorOptionForUpdate
): option is ExistingSchemaFieldSelectorOption {
  return (option as ExistingSchemaFieldSelectorOption).id !== undefined;
}

function validateOptionName(
  initialName: string,
  newName: string,
  existingOptions: Array<SchemaFieldSelectorOptionForUpdate>
): boolean {
  // Used when editing an existing selector (options can only be edited/added one by one)
  const sanitizedNewName = newName.trim();
  if (
    initialName !== sanitizedNewName &&
    existingOptions.some((option) => option.name === sanitizedNewName)
  ) {
    notifications.error('You cannot have two options with the same name');
    return false;
  }
  return true;
}

export interface OptionRowDataProps extends DraggableAliasFieldProps {
  archive: (() => void) | null;
  className: string;
  complete: ((newAlias: string) => void) | null;
  id: string;
  initialAlias: string;
  isWritable: boolean;
  placeholder: string;
  remove: ((name: string) => void) | null;
  unarchive: ((name: string) => void) | null;
  validate: (initialName: string, newName: string) => boolean;
}

export interface SchemaFieldSelectorOptionsSettingsProps {
  isWritable: () => boolean;
  selectedArchivePurposes: Array<string>;
  handleChangeSelectedArchivePurposes: (purposes: Array<string>) => void;
  options: Array<SchemaFieldSelectorOptionForUpdate>;
  setOptions: (options: Array<SchemaFieldSelectorOptionForUpdate>) => void;
  selectorId?: string;
  updateSelector: (selectorId: string | null, options: Array<SchemaFieldSelectorOptionForUpdate>) => void;
}

export default function SchemaFieldSelectorOptionsSettings({
  isWritable,
  selectedArchivePurposes,
  handleChangeSelectedArchivePurposes,
  options,
  setOptions,
  selectorId,
  updateSelector,
}: SchemaFieldSelectorOptionsSettingsProps): JSX.Element {
  const [isOptionBeingCreated, setIsOptionBeingCreated] = useState(false);

  const hideNewOptionField = (index: number): void => {
    if (selectorId) {
      setIsOptionBeingCreated(false);
    } else {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const createOption = (name: string, index: number): void => {
    hideNewOptionField(index);
    const newOptions = [...options, {name: name.trim()}];
    setOptions(newOptions);
    if (selectorId) {
      updateSelector(null, newOptions);
    }
  };

  const handleStartCreatingOption = (): void => {
    if (selectorId) {
      setIsOptionBeingCreated(true);
    } else {
      setOptions([...options, {name: ''}]);
    }
  };

  const handleImportOptions = async (): Promise<void> => {
    const existingOptionNames = options.map((option: SchemaFieldSelectorOptionForUpdate) => option.name);
    const newOptionNames = await runModal(ImportSchemaFieldSelectorOptionsModal, {existingOptionNames});
    if (newOptionNames == null) {
      return;
    }
    const addedOptions = newOptionNames.map((name) => ({name}));
    const newOptions = [...options, ...addedOptions];
    if (selectorId) {
      updateSelector(null, newOptions);
    } else {
      setOptions(newOptions);
    }
  };

  const handleAlphabetizeOptions = (): void => {
    const sortedOptions = naturalSortByKey(options, 'name');
    if (selectorId) {
      updateSelector(null, sortedOptions);
    } else {
      setOptions(sortedOptions);
    }
  };

  const getVisibleExistingOptions = (): Array<ExistingSchemaFieldSelectorOption> => {
    const visibleOptions = ArchiveFilterUtil.filterItemsToPurposes(
      filterToExistingSchemaFieldSelectorOptions(options),
      selectedArchivePurposes.flat()
    );
    if (visibleOptions == null) {
      return [];
    }
    const archivedVisibleOptions = visibleOptions.filter((option) => isArchived(option));
    const notArchivedVisibleOptions = visibleOptions.filter((option) => !isArchived(option));
    return [...notArchivedVisibleOptions, ...archivedVisibleOptions];
  };

  const filterToExistingSchemaFieldSelectorOptions = (
    options: Array<SchemaFieldSelectorOptionForUpdate>
  ): Array<ExistingSchemaFieldSelectorOption> =>
    options.filter((option): option is ExistingSchemaFieldSelectorOption =>
      isExistingSchemaFieldSelectorOption(option)
    );

  const handleArchiveOption = (itemIds: Array<string>, purpose: string): void => {
    assert(itemIds.length === 1, 'Expected only one selector option');
    const newOptions = options.map((option) =>
      isExistingSchemaFieldSelectorOption(option) && option.id === itemIds[0]
        ? {...option, archiveRecord: {...option.archiveRecord, purpose}}
        : option
    );
    updateSelector(null, newOptions);
  };

  const handleSetNewOptionName = (index: number, name: string): void => {
    setOptions(options.map((option, i) => (i === index ? {...option, name} : option)));
  };

  const handleOpenArchiveModal = (option: ExistingSchemaFieldSelectorOption): void => {
    ArchiveModal.open({items: [option], onArchive: handleArchiveOption});
  };

  const unarchiveOption = (name: string): void => {
    const newOptions = options.map((option) =>
      option.name === name ? {...option, archiveRecord: null} : option
    );
    updateSelector(null, newOptions);
  };

  const removeOption = (name: string): void => {
    const newOptions = options.filter((option) => option.name !== name);
    updateSelector(null, newOptions);
  };

  const renameOption = (initialName: string, newName: string): void => {
    if (initialName === newName) {
      return;
    }
    const newOptions = options.map((option) =>
      option.name === initialName ? {...option, name: newName.trim()} : option
    );
    setOptions(newOptions);
    updateSelector(null, newOptions);
  };

  const getRowDataForOptions = (): Array<ItemData<OptionRowDataProps>> =>
    getVisibleExistingOptions().map((option: ExistingSchemaFieldSelectorOption) => ({
      id: option.id,
      itemProps: {
        archive: isArchived(option)
          ? null
          : () => {
              handleOpenArchiveModal(option);
            },
        className: classNames('selectorSettings-field', {'u-faded': isArchived(option)}),
        complete: isArchived(option)
          ? null
          : (newAlias: string) => {
              renameOption(option.name, newAlias);
            },
        id: option.id,
        initialAlias: option.name,
        isEditable: isWritable() && !isArchived(option),
        isWritable: isWritable(),
        placeholder: 'Enter option name',
        remove: isArchived(option) || BenchlingConfig.ENABLE_ARCHIVING_V2 ? null : removeOption,
        unarchive: isArchived(option) ? unarchiveOption : null,
        validate: (initialName: string, newName: string) => validateOptionName(initialName, newName, options),
        isFullWidth: true,
        onDragStart: noop,
      },
      itemClass: DraggableAliasField,
    }));

  const handleMoveOption = (newVisibleOrderedIds: Array<string>): void => {
    const allOptionsById = objectFromEntries(
      options.map((option: ExistingSchemaFieldSelectorOption) => [option.id, option])
    );
    const hiddenOrderedIds = options
      .map((option: ExistingSchemaFieldSelectorOption) => option.id)
      .filter((id: string) => !newVisibleOrderedIds.includes(id));
    const newOrderOptions = [...newVisibleOrderedIds, ...hiddenOrderedIds].map((id) => allOptionsById[id]);
    setOptions(newOrderOptions);
  };

  const handleMoveOptionComplete = (): void => {
    if (selectorId) {
      updateSelector(null, options);
    }
  };

  const optionsComponent = selectorId ? (
    <ReorderableList
      itemDatas={getRowDataForOptions()}
      onMoveItem={handleMoveOption}
      onMoveItemEnd={handleMoveOptionComplete}
    />
  ) : (
    options.map((option: SchemaFieldSelectorOptionForUpdate, i: number) => (
      <div className="selectorSettings-row" key={i}>
        <NewSelectorOptionField
          index={i}
          onRemove={hideNewOptionField}
          onChange={handleSetNewOptionName}
          value={option.name}
        />
      </div>
    ))
  );

  return (
    <div>
      <div>
        <div className="metadata-header">
          <div className="u-lr-split">
            <div>
              Options
              {isWritable() && <AddIcon onClick={handleStartCreatingOption} />}
            </div>
            {isWritable() && (
              <div>
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  onClick={handleImportOptions}
                  size={ButtonSize.MINI}
                  text="Import Options"
                />
                <Button
                  className="pull-right u-mini-spacing-left"
                  buttonStyle={ButtonStyle.SECONDARY}
                  disabled={isOptionBeingCreated || !options?.length}
                  onClick={handleAlphabetizeOptions}
                  size={ButtonSize.MINI}
                  text="Alphabetize"
                />
              </div>
            )}
          </div>
          <div className="u-flex-display u-small-spacing-top">
            <ArchivePurposeDropdown
              itemIds={options.map((option: ExistingSchemaFieldSelectorOption) => option.id)}
              onChangePurposes={handleChangeSelectedArchivePurposes}
              selectedPurposes={selectedArchivePurposes}
            />
          </div>
        </div>
      </div>
      {optionsComponent}
      <div>
        {isOptionBeingCreated && (
          <AliasField
            cancel={hideNewOptionField}
            className="selectorSettings-field"
            complete={createOption}
            initialIsBeingEdited={true}
            isWritable={isWritable()}
            key="new option"
            placeholder="Enter option name"
            validate={(initialName: string, newName: string) =>
              validateOptionName(initialName, newName, options)
            }
          />
        )}
      </div>
      <div>{options.length === 0 && !isOptionBeingCreated && 'This dropdown has no options'}</div>
    </div>
  );
}

interface NewSelectorOptionFieldProps {
  index: number;
  onChange: (index: number, name: string) => void;
  onRemove: (index: number) => void;
  value: string;
}

function NewSelectorOptionField({
  index,
  value,
  onChange,
  onRemove,
}: NewSelectorOptionFieldProps): JSX.Element {
  return (
    <div className="selectorSettings-creatingOptionRow">
      <div className="metadata-field-col">
        <FormInput
          autoFocus={true}
          onChange={(e: InputChangeEvent) => {
            onChange(index, e.target.value);
          }}
          placeholder="Enter option name"
          value={value}
        />
      </div>
      <FieldActionButtons
        isEditing={false}
        remove={() => {
          onRemove(index);
        }}
      />
    </div>
  );
}
